import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { FileTypeDatasetAdapter, formatAdapts, compressionAdapts } from './file-type.dataset.adapter';
import { AzureDataLakeStoreFileDataset } from 'app/model/dataset/azure-data-lake-store.dataset';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned } from 'app/model/base/named';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataLakeStoreFileDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                folderPath: '*',
                fileName: '*',
                wildcardPath: '*',
                modifiedDatetimeStart: '*',
                modifiedDatetimeEnd: '*',
                format: formatAdapts,
                compression: compressionAdapts,
                generatedFromActivity: '*'
            }
        }
    }
})
export class AzureDataLakeStoreFileDatasetAdapter extends FileTypeDatasetAdapter<AzureDataLakeStoreFileDataset, Model.DatasetResource> {
    protected async _toResource(dataset: AzureDataLakeStoreFileDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.AzureDataLakeStoreDataset;
        modelDataset.type = Metamodel.modelFor(AzureDataLakeStoreFileDataset).name;
        modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        modelDataset.typeProperties = {
            ...this.formatAndCompressionValueToResource(dataset.format, dataset.compression),
            fileName: ValueAdapter.toResource(dataset, AzureDataLakeStoreFileDataset.FILE_NAME),
            folderPath: ValueAdapter.toResource(dataset, AzureDataLakeStoreFileDataset.FOLDER_PATH),
            generatedFromActivity: dataset.generatedFromActivity,
            wildcardPath: ValueAdapter.toResource(dataset, AzureDataLakeStoreFileDataset.WILDCARD_PATH),
            modifiedDatetimeStart: ValueAdapter.toResource(dataset, AzureDataLakeStoreFileDataset.MODIFID_DATETIME_START),
            modifiedDatetimeEnd: ValueAdapter.toResource(dataset, AzureDataLakeStoreFileDataset.MODIFID_DATETIME_END)
        };
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: AzureDataLakeStoreFileDataset): Promise<AzureDataLakeStoreFileDataset> {
        if (!dataset) {
            dataset = AzureDataLakeStoreFileDataset.MODEL.create<AzureDataLakeStoreFileDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.AzureDataLakeStoreDataset;
        if (model) {
            if (model.typeProperties != null) {
                Owned.own(dataset, 'format', this.formatResourceToValue(model.typeProperties.format));
                dataset.compression = this.compressionResourceToValue(model.typeProperties.compression);
                ValueAdapter.toModel(dataset, AzureDataLakeStoreFileDataset.WILDCARD_PATH, model.typeProperties.wildcardPath);
                ValueAdapter.toModel(dataset, AzureDataLakeStoreFileDataset.FILE_NAME, model.typeProperties.fileName);
                ValueAdapter.toModel(dataset, AzureDataLakeStoreFileDataset.FOLDER_PATH, model.typeProperties.folderPath);
                ValueAdapter.toModel(dataset, AzureDataLakeStoreFileDataset.MODIFID_DATETIME_START, model.typeProperties.modifiedDatetimeStart);
                ValueAdapter.toModel(dataset, AzureDataLakeStoreFileDataset.MODIFID_DATETIME_END, model.typeProperties.modifiedDatetimeEnd);
                dataset.generatedFromActivity = model.typeProperties.generatedFromActivity;
            } else {
                this.initializeModel(dataset);
            }
            dataset.columns = this.columnResourceToValue(dataset, model.structure);
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, AzureDataLakeStoreFileDataset.MODEL));
        return dataset;
    }
}
